/**
 * 월간 보고서 자동 발송(261030-report-auto-dispatch) — 구독 기간 끝날에 보고서를 고정 저장하고 알림톡으로 링크를 보낸다.
 *
 * ★ 공개 보고서(`viewReportShare`)는 **저장한 스냅샷**을 돌려준다 — 다시 계산하지 않는다(보낸 숫자 = 보이는 숫자).
 * ★ 토큰은 **body 로만** 받는다(경로·쿼리 계약에 싣지 않는다 — 결제 링크와 같은 규칙, 261020-pay-link).
 * ★ 공개 뷰에 표본(`k/n`)·내부 id 를 싣지 않는다 — 고객 화면 규칙(260921-client-report-chart-only). 그래프의 띠(lo/hi)는 띠로만 쓴다.
 */
import { z } from 'zod';
import { BrandKindQuery, IdParams, IsoDateTime, KstDate } from '../common.ts';
import { defineRoute } from '../route.ts';

export const ReportShareToken = z.string().min(20).max(200);

const DayPoint = z.object({ rate: z.number(), lo: z.number(), hi: z.number() }).nullable();

/** 보고서 화면 한 장 — 보고서 이미지(`reportCard`)와 같은 모델을 고객용으로 좁힌 것 */
export const ReportPageView = z.object({
  brandName: z.string(),
  /** `10월` */
  monthLabel: z.string(),
  /** `2026.09.20 — 10.19` */
  period: z.string(),
  /** 내일 이어지는가 · 오늘로 끝나는가 */
  kind: z.enum(['renewal', 'ending']),
  /** 갱신일(내일) 또는 종료일(오늘) */
  nextOn: KstDate,
  works: z.array(
    z.object({
      key: z.enum(['homepage', 'post', 'press', 'video', 'review']),
      n: z.number().int(),
      unit: z.string(),
      label: z.string(),
      detail: z.string().nullable(),
    }),
  ),
  highlights: z.array(
    z.object({
      kind: z.enum(['press', 'video', 'post', 'review']),
      title: z.string(),
      source: z.string().nullable(),
      excerpt: z.string().nullable(),
      /** 발송 때 받아 둔 썸네일(data URI) — null 이면 일러스트 */
      image: z.string().nullable(),
    }),
  ),
  result: z
    .object({
      queries: z.array(
        z.object({
          text: z.string(),
          rate: z.number().int().nullable(),
          first: z.object({ index: z.number().int(), rate: z.number().int(), label: z.string() }).nullable(),
          recent: z
            .object({
              rate: z.number().int(),
              fromIndex: z.number().int(),
              toIndex: z.number().int(),
              label: z.string(),
            })
            .nullable(),
          headline: z
            .object({
              from: z.number().int().nullable(),
              value: z.number().int(),
              fromFirst: z.boolean(),
              caption: z.string(),
            })
            .nullable(),
          series: z.array(DayPoint),
        }),
      ),
      days: z.number().int(),
      ticks: z.array(z.object({ index: z.number().int(), label: z.string() })),
    })
    .nullable(),
});
export type ReportPageView = z.infer<typeof ReportPageView>;

export const viewReportShare = defineRoute({
  method: 'post',
  path: '/api/report-shares/view',
  access: 'public',
  summary: '월간 보고서 공유 링크 — 토큰으로 발송 때 고정한 보고서를 연다. 만료·없음은 400 하나',
  body: z.object({ token: ReportShareToken }).strict(),
  response: ReportPageView,
});

// ── 운영자 ────────────────────────────────────────────────────────────────

export const REPORT_DISPATCH_STATUSES = ['claimed', 'ready', 'sent', 'failed', 'skipped'] as const;

export const ReportDispatchRow = z.object({
  id: z.uuid(),
  brandId: z.uuid(),
  brandName: z.string(),
  kind: z.enum(['renewal', 'ending']),
  cycleFrom: KstDate.nullable(),
  cycleTo: KstDate,
  nextOn: KstDate,
  status: z.enum(REPORT_DISPATCH_STATUSES),
  attempts: z.number().int(),
  lastError: z.string().nullable(),
  /** 스냅샷이 있어 운영자가 열어 볼 수 있다 */
  viewable: z.boolean(),
  expiresAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
  notices: z.array(
    z.object({
      at: IsoDateTime,
      recipient: z.string().nullable(),
      status: z.enum(['sent', 'failed', 'skipped']),
      detail: z.string().nullable(),
    }),
  ),
});
export type ReportDispatchRow = z.infer<typeof ReportDispatchRow>;

export const listReportDispatches = defineRoute({
  method: 'get',
  path: '/api/admin/report-dispatches',
  access: 'admin',
  summary: '월간 보고서 자동 발송 기록 — 최근 순(기본 최근 60일)',
  query: BrandKindQuery.extend({ days: z.coerce.number().int().min(1).max(400).optional() }),
  response: z.object({
    /** 자동 발송이 켜져 있나(`--report-dispatch`) — 꺼져 있으면 화면이 그렇게 적는다 */
    enabled: z.boolean(),
    items: z.array(ReportDispatchRow),
  }),
});

export const viewReportDispatch = defineRoute({
  method: 'get',
  path: '/api/admin/report-dispatches/{id}/view',
  access: 'admin',
  summary: '보낸 보고서 그대로 보기(운영자)',
  params: IdParams,
  response: ReportPageView,
});

export const resendReportDispatch = defineRoute({
  method: 'post',
  path: '/api/admin/report-dispatches/{id}/resend',
  access: 'admin',
  summary: '알림톡 다시 보내기 — 새 링크를 발급한다(옛 링크는 죽는다). 스냅샷이 없으면 먼저 만든다',
  params: IdParams,
  response: z.object({ sent: z.number().int(), recorded: z.number().int() }),
});
